// src/components/Sidebar.jsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useRol } from "../hooks/useRoles";

const BG      = "#3A3C7A";
const BG_ITEM = "#2e3066";
const BORDER  = "#4a4c8a";
const ORANGE  = "#F97316";

const MENU = [
  {
    grupo: "Principal",
    items: [
      { to: "/",            label: "Panel",            icon: "M1 1h5v5H1zM8 1h5v5H8zM1 8h5v5H1zM8 8h5v5H8z" },
      { to: "/midia",       label: "Mi día",           icon: "M7 1a6 6 0 100 12A6 6 0 007 1zM4.5 7l2 2 3-3" },
      { to: "/clientes",    label: "Clientes",          icon: "M9 2a4 4 0 100 8 4 4 0 000-8zM2 13c0-3 3-5 7-5s7 2 7 5" },
      { to: "/cotizaciones",    label: "Cotizaciones",   icon: "M2 3h10l1 9H1L2 3zM5 7h4M7 5v4" },
      { to: "/cotizacion-nueva", label: "Nueva cotiz. IA", icon: "M7 1l1.5 4h4l-3 2.5 1 4L7 9l-3.5 2.5 1-4L1 5h4z" },
      { to: "/pedidos",     label: "Pedidos",           icon: "M2 3h10l1 9H1L2 3zM5 3V2a2 2 0 014 0v1" },
      { to: "/seguimiento", label: "Seguimiento",       icon: "M12 2l1 4H1L2 2h10zM7 6v6M4 9h6" },
      { to: "/reuniones",   label: "Reuniones",         icon: "M2 3h10a1 1 0 011 1v6a1 1 0 01-1 1H7l-3 2v-2H2a1 1 0 01-1-1V4a1 1 0 011-1z" },
      { to: "/pipeline",    label: "Embudo de ventas",  icon: "M1 2h12L8 8v4l-2 1V8L1 2z" },
    ],
  },
  {
    grupo: "Operaciones",
    items: [
      { to: "/transporte",  label: "Transporte",  icon: "M1 8h9M1 8V5l3-3h5l2 3v3M1 8v3h11V8M4 11a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM9 11a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" },
      { to: "/proveedores", label: "Proveedores", icon: "M2 12L7 2L12 12M4 8h6", permiso: "verProveedores" },
    ],
  },
  {
    grupo: "Administración",
    items: [
      { to: "/reportes", label: "Reportes", icon: "M2 10h10M2 7h10M2 4h6M9 9l2 2 3-3", permiso: "verProveedores" },
      { to: "/usuarios", label: "Usuarios",  icon: "M7 5a2 2 0 100 4 2 2 0 000-4zM2 12C2 9.5 12 9.5 12 12", permiso: "verUsuarios" },
      { to: "/monitoreo", label: "Monitoreo", icon: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5", permiso: "verMonitoreo" },
    ],
  },
];

function Icon({ d, color = "#fff", size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      <path d={d} />
    </svg>
  );
}

function HamburgerIcon({ color = "#fff" }) {
  return (
    <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
      <line x1="0" y1="1"  x2="18" y2="1"  stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <line x1="0" y1="7"  x2="18" y2="7"  stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <line x1="0" y1="13" x2="18" y2="13" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

export default function Sidebar({ isMobile }) {
  const { logout, user }                   = useAuth();
  const { pathname }                       = useLocation();
  const { rol, perfil, permisos, loading } = useRol();
  const [mobileOpen, setMobileOpen]        = useState(false);
  const [urgentesHoy, setUrgentesHoy]      = useState(0);

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  // Leer badge de urgentes desde localStorage cada vez que cambia la ruta
  useEffect(() => {
    if (!user?.uid) return;
    const n = parseInt(localStorage.getItem(`midia_urgentes_${user.uid}`) || "0", 10);
    setUrgentesHoy(isNaN(n) ? 0 : n);
  }, [pathname, user?.uid]);

  const iniciales = (perfil?.nombre || user?.email || "U")
    .split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();

  const menuFiltrado = MENU.map(g => ({
    ...g,
    items: g.items.filter(i => !i.permiso || (!loading && permisos[i.permiso]))
  })).filter(g => g.items.length > 0);

  // ── MÓVIL — botón flotante + drawer ───────────────────────────────
  if (isMobile) {
    return (
      <>
        {/* Botón hamburguesa flotante */}
        {!mobileOpen && (
          <button
            onClick={() => setMobileOpen(true)}
            style={{
              position: "fixed",
              top: 12,
              left: 12,
              zIndex: 1000,
              width: 40,
              height: 40,
              background: BG,
              border: "none",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <HamburgerIcon />
          </button>
        )}

        {/* Drawer */}
        {mobileOpen && (
          <div
            className="modal-overlay"
            style={{ position: "fixed", inset: 0, zIndex: 999, display: "flex" }}
            onClick={() => setMobileOpen(false)}
          >
            <div
              className="drawer-enter"
              style={{
                width: 260, height: "100%", background: BG,
                display: "flex", flexDirection: "column",
                borderRight: `1px solid ${BORDER}`,
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "14px 14px 12px", borderBottom: `1px solid ${BORDER}`,
              }}>
                <img src="/logo.png" alt="PROVEXO+" style={{ height: 40, objectFit: "contain", flex: 1 }} />
                <button onClick={() => setMobileOpen(false)} style={{
                  background: BG_ITEM, border: "none", borderRadius: 6,
                  width: 30, height: 30, cursor: "pointer", color: "#fff",
                  fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
                }}>✕</button>
              </div>

              {/* Links */}
              <nav style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
                {menuFiltrado.map(g => (
                  <div key={g.grupo}>
                    <div style={{
                      fontSize: 9, fontWeight: 700, color: "#c7d2fe",
                      textTransform: "uppercase", letterSpacing: "0.08em",
                      padding: "8px 16px 4px",
                    }}>{g.grupo}</div>
                    {g.items.map(item => {
                      const isOn   = pathname === item.to;
                      const badge  = item.to === "/midia" && urgentesHoy > 0 && !isOn;
                      return (
                        <Link key={item.to} to={item.to} style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "10px 12px", margin: "1px 8px",
                          borderRadius: 6, textDecoration: "none",
                          fontSize: 14, fontWeight: 500,
                          background: isOn ? ORANGE : "transparent",
                          color: isOn ? "#000" : "#fff",
                        }}>
                          <Icon d={item.icon} color={isOn ? "#000" : "#fff"} size={16} />
                          {item.label}
                          {badge && (
                            <span style={{ marginLeft: "auto", background: "#DC2626", color: "#fff", fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 99, lineHeight: 1.6 }}>
                              {urgentesHoy}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                    <div style={{ height: "0.5px", background: BORDER, margin: "6px 14px" }} />
                  </div>
                ))}
              </nav>

              {/* Usuario */}
              <div style={{
                padding: "12px 14px", borderTop: `1px solid ${BORDER}`,
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <div style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: ORANGE, color: "#000",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 700, flexShrink: 0,
                }}>{iniciales}</div>
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {perfil?.nombre || user?.email}
                  </div>
                  <div style={{ fontSize: 10, color: "#c7d2fe" }}>
                    {rol === "admin" ? "👑 Admin" : "💼 Asesor"}
                  </div>
                </div>
                <button onClick={logout} style={{
                  background: BG_ITEM, border: "none", borderRadius: 6,
                  padding: "6px 10px", fontSize: 11, color: "#fff", cursor: "pointer",
                }}>Salir</button>
              </div>
            </div>

            {/* Overlay oscuro */}
            <div style={{ flex: 1, background: "rgba(0,0,0,0.6)" }} />
          </div>
        )}
      </>
    );
  }

  // ── DESKTOP — sidebar siempre visible ─────────────────────────────
  return (
    <>
    <style>{`
      .sb-link {
        transition: background 150ms cubic-bezier(0.23,1,0.32,1),
                    color 150ms cubic-bezier(0.23,1,0.32,1),
                    transform 150ms cubic-bezier(0.23,1,0.32,1),
                    box-shadow 200ms cubic-bezier(0.23,1,0.32,1);
      }
      .sb-link:hover:not(.sb-on) {
        background: rgba(255,255,255,0.09) !important;
        transform: translateX(2px);
      }
      .sb-link:active { transform: scale(0.96) !important; }
      .sb-on {
        box-shadow: -3px 0 0 rgba(255,255,255,0.6),
                    0 2px 12px rgba(249,115,22,0.35),
                    inset 0 0 0 1px rgba(255,255,255,0.10);
      }
      .sb-logout {
        transition: background 150ms cubic-bezier(0.23,1,0.32,1),
                    transform 100ms cubic-bezier(0.23,1,0.32,1);
      }
      .sb-logout:hover { background: rgba(255,255,255,0.15) !important; }
      .sb-logout:active { transform: scale(0.93) !important; }
      .sb-badge {
        animation: sb-pulse 2s ease-in-out infinite;
      }
      @keyframes sb-pulse {
        0%,100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.5); }
        50%      { box-shadow: 0 0 0 4px rgba(220,38,38,0); }
      }
    `}</style>
    <aside style={{
      width: 220,
      minHeight: "100vh",
      background: `linear-gradient(180deg, #3A3C7A 0%, #2e3066 100%)`,
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      borderRight: `1px solid ${BORDER}`,
    }}>

      {/* Logo */}
      <div style={{
        padding: "14px 14px 12px",
        borderBottom: `1px solid ${BORDER}`,
        display: "flex", alignItems: "center",
        justifyContent: "center",
        minHeight: 68,
      }}>
        <img src="/logo.png" alt="PROVEXO+" style={{ height: 44, width: "auto", objectFit: "contain" }} />
      </div>

      {/* Links */}
      <nav style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
        {menuFiltrado.map(g => (
          <div key={g.grupo}>
            <div style={{
              fontSize: 9, fontWeight: 700, color: "#c7d2fe",
              textTransform: "uppercase", letterSpacing: "0.08em",
              padding: "8px 16px 4px",
            }}>{g.grupo}</div>
            {g.items.map(item => {
              const isOn  = pathname === item.to;
              const badge = item.to === "/midia" && urgentesHoy > 0 && !isOn;
              return (
                <Link key={item.to} to={item.to}
                  className={`sb-link${isOn ? " sb-on" : ""}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px", margin: "1px 8px",
                    borderRadius: 6, textDecoration: "none",
                    fontSize: 13, fontWeight: isOn ? 600 : 500,
                    background: isOn ? ORANGE : "transparent",
                    color: isOn ? "#000" : "#fff",
                  }}>
                  <Icon d={item.icon} color={isOn ? "#000" : "#fff"} />
                  {item.label}
                  {badge && (
                    <span className="sb-badge" style={{ marginLeft: "auto", background: "#DC2626", color: "#fff", fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 99, lineHeight: 1.6 }}>
                      {urgentesHoy}
                    </span>
                  )}
                </Link>
              );
            })}
            <div style={{ height: "0.5px", background: BORDER, margin: "6px 14px" }} />
          </div>
        ))}
      </nav>

      {/* Usuario */}
      <div style={{
        padding: "12px 14px",
        borderTop: `1px solid ${BORDER}`,
        display: "flex", alignItems: "center", gap: 8,
        background: "rgba(0,0,0,0.15)",
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: "50%",
          background: ORANGE, color: "#000",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 11, fontWeight: 700, flexShrink: 0,
          boxShadow: "0 2px 8px rgba(249,115,22,0.4)",
        }}>{iniciales}</div>
        <div style={{ flex: 1, overflow: "hidden" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {perfil?.nombre || user?.email}
          </div>
          <div style={{ fontSize: 9, color: "#c7d2fe" }}>
            {rol === "admin" ? "👑 Admin" : "💼 Asesor"}
          </div>
        </div>
        <button onClick={logout} className="sb-logout" style={{
          background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 6,
          padding: "5px 8px", fontSize: 10, color: "#fff",
          cursor: "pointer", flexShrink: 0,
        }}>Salir</button>
      </div>
    </aside>
    </>
  );
}
